import React, { useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import styled from 'styled-components';
import { animated, useSpring } from '@react-spring/web';
import classNames from 'classnames';
import { NavLink } from 'react-router-dom';
import { ButtonProps, RoundButton } from '../../../button';
import Icon from '../../../icon';
import Panel from '../../../panel';
import { Div, Flex } from '../../../box';
import { H6 } from '../../../title';
import { colors, darken } from '../../../../style';
import { actionColor, actionLight } from '../../../../style/theme-helpers';
import materialIcons from '../../../icon/constants/material-icons-constants';

const AnimatedDropdownContent = animated(DropdownMenu.Content);
const AnimatedPanel = animated(Panel);

export function IslandsSwitch({ children }: { children?: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const handleContentClick = () => {
    setIsOpen(false);
    // remove the element from the DOM
    document.getElementsByClassName('island-switch-dropdown')[0]?.remove();
  };

  const springProps = useSpring({
    opacity: isOpen ? 1 : 0,
    scale: isOpen ? 1 : 0.95,
    config: { tension: 300, friction: 25 },
  });

  return (
    <DropdownMenu.Root
      open={isOpen}
      onOpenChange={open => {
        setIsOpen(open);
      }}
      modal={false}
    >
      <DropdownMenu.Trigger asChild>
        <RoundButton active={isOpen} size={'auto'} aria-label={'apps'} variant={'secondary-clean'} data-testid={'navbar-apps-trigger'}>
          <Div padding={'xs'}>
            <Icon name={'APPS'} size={'2xxl'} />
          </Div>
        </RoundButton>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <AnimatedDropdownContent
          side={'bottom'}
          className={'island-switch-dropdown'}
          style={{
            zIndex: 2000,
            opacity: springProps.opacity,
            transform: springProps.scale.to(s => `scale(${s})`),
            transformOrigin: 'top center',
          }}
          onClick={handleContentClick}
        >
          <AnimatedPanel
            variant={'shadow'}
            display={'flex'}
            flexWrap={'wrap'}
            borderRadius={'md'}
            padding={'xs'}
            maxWidth={430}
            width={'100%'}
            gap={'xs'}
            justifyContent={'space-between'}
          >
            {children}
          </AnimatedPanel>
        </AnimatedDropdownContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

const StyledDropdownMenuItem = styled(DropdownMenu.Item)`
  outline: none;
  width: ${128}px;
  height: ${128}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const StyledIslandsSwitcherNavButton = styled(NavLink)<IslandsSwitchNavButtonProps>`
  color: ${colors.gray900};
  text-decoration: none;
  font-size: 1rem;
  display: flex;
  justify-content: center;
  border: 1px solid transparent;
  padding: 8px 8px;
  align-items: center;
  border-radius: 16px;
  font-weight: 400;
  background: transparent;
  transition: 0.3s;

  &:last-child {
    margin-right: auto;
  }

  &:hover:not([disabled]) {
    background: ${props => props.theme.actionLight};
  }

  &:focus-visible {
    background: ${props => props.theme.actionLight};
    border: 1px solid transparent;
  }

  &:active,
  &:active:hover {
    background: ${props => darken(0.05, actionLight(props))};
  }

  &:not([disabled]) {
    cursor: pointer;
  }

  &[disabled] {
    cursor: not-allowed;
    background: ${colors.gray400};
    color: ${colors.gray900};
  }

  &.active,
  &:hover {
    background: ${actionLight};
    color: ${actionColor} !important;
  }

  &.active {
    &,
    > * {
      font-weight: 500;
    }
  }
`;

export interface IslandsSwitchNavButtonProps extends ButtonProps {
  className?: string;
  to?: string;
  children?: React.ReactNode | string;
  href?: string;
  componentClass?: React.ElementType;
  disabled?: boolean;
  exact?: boolean;
}
const IslandsSwitcherNavButton = ({ className, to, href, children, ...rest }: IslandsSwitchNavButtonProps) => {
  return (
    // @ts-ignore
    <StyledIslandsSwitcherNavButton
      className={classNames('navbar-button', className)}
      as={href ? 'a' : to ? NavLink : undefined}
      to={to}
      weight={400}
      href={href}
      {...rest}
    >
      {children}
    </StyledIslandsSwitcherNavButton>
  );
};
export const IslandItem = ({
  icon,
  label,
  to,
  href,
  onClick,
  target,
  'data-testid': dataTestId,
}: {
  label: React.ReactNode;
  to?: string;
  href?: string;
  onClick?: () => void;
  icon: keyof typeof materialIcons;
  target?: '_blank' | '_self';
  'data-testid'?: string;
}) => {
  {
    /* items are initially rendered with focus state by design: https://github.com/radix-ui/primitives/issues/1164 */
  }
  return (
    <StyledDropdownMenuItem asChild>
      <IslandsSwitcherNavButton to={to} href={href} target={target} onClick={onClick} data-testid={dataTestId}>
        <Flex height={'100%'} flexDirection={'column'} alignItems={'center'} justifyContent={'center'} gap={'sm'}>
          <Icon name={icon} weight={200} size={'4xxl'} outlined variant={'inherit'} />
          <DropdownMenu.Label asChild>
            <H6
              align={'center'}
              variant={'inherit'}
              weight={'inherit'}
              style={{ width: '100%', overflowWrap: 'break-word' }}
            >
              {label}
            </H6>
          </DropdownMenu.Label>
        </Flex>
      </IslandsSwitcherNavButton>
    </StyledDropdownMenuItem>
  );
};
